import * as Dialog from "@radix-ui/react-dialog";
import { useState } from "react";
import { ReactComponent as DateRangeIcon } from "../../assets/img/events/date.svg";
import { ReactComponent as CloseIcon } from "assets/img/icons/header/close.svg";
import { Button } from "components/elements";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";
import { dateTimeRangeParts } from "../events/types";
import type { CalendarFriendlyCompassEvent } from "./types";
import { DeleteCompassEventModal } from "./DeleteCompassEventModal";

type CompassEventDetailsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    event: CalendarFriendlyCompassEvent;
    onEdit: (event: CalendarFriendlyCompassEvent) => void;
};

export function CompassEventDetailsModal({
    isOpen,
    onClose,
    event,
    onEdit,
}: CompassEventDetailsModalProps) {
    const timeZone = useUserTimezone();
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const utils = trpc.useContext();
    const range = dateTimeRangeParts(event.start, event?.end, timeZone);

    const { mutate: deleteEvent, isPending: isDeleting } = trpc.compassEvents.delete.useMutation({
        onSuccess: () => {
            utils.compassEvents.latest.invalidate();
            utils.compassEvents.allFutureEvents.invalidate();
            onClose();
        },
    });

    const handleDelete = () => {
        deleteEvent({ compassEventId: event.id });
    };

    return (
        <>
            <Dialog.Root open={isOpen} onOpenChange={onClose}>
                <Dialog.Overlay className="fixed inset-0 z-50 bg-[#424242]/75" />
                <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[460px] max-w-[90%] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-none">
                    <div className="mb-2 flex items-center justify-between">
                        <Dialog.Title asChild>
                            <h2 className="font-sans text-[24px] font-bold leading-[100%] text-[#424242]">
                                Event Details
                            </h2>
                        </Dialog.Title>
                        <Dialog.Close>
                            <CloseIcon className="-mt-2 h-[30px] w-[30px]" />
                        </Dialog.Close>
                    </div>
                    <Dialog.Description className="sr-only">
                        View and manage event details including title, date range, and description
                    </Dialog.Description>

                    <hr className="-mx-[24px] my-4 border-0 border-b border-[#c8c8c8] shadow-none" />

                    <h3 className="mb-3 font-sans text-[22px] font-semibold leading-normal tracking-normal text-[#424242]">
                        {event.title}
                    </h3>

                    {range && (
                        <div className="mb-4">
                            <div className="flex items-start gap-2 rounded bg-[#dff1fa] px-3 py-2 text-[#424242]">
                                <DateRangeIcon className="mt-[2px] h-4 w-4 shrink-0 text-gray-600" />
                                <div className="flex flex-col leading-tight">
                                    <span className="text-[14px] font-medium">{range.primary}</span>
                                    {range.secondary && (
                                        <span className="text-[13px] opacity-80">
                                            {range.secondary}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {event.description && (
                        <div className="mb-4">
                            <h4 className="mb-1 font-sans text-[18px] font-semibold leading-normal tracking-normal text-[#424242]">
                                Description
                            </h4>
                            <div className="mt-3 rounded-lg bg-[#eeeeee] p-4 font-sans text-[14px] font-normal leading-[127%] text-[#424242]/90">
                                {event.description}
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end gap-3">
                        <Button
                            onClick={() => setIsDeleteModalOpen(true)}
                            variant="outline"
                            isLoading={isDeleting}
                            className="border-red-500 text-red-500 hover:bg-red-50"
                        >
                            Delete
                        </Button>
                        <Button onClick={() => onEdit(event)} color="blue" variant="primary">
                            Edit
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Root>
            <DeleteCompassEventModal
                isOpen={isDeleteModalOpen}
                setIsOpen={setIsDeleteModalOpen}
                onDelete={handleDelete}
                compassEventTitle={event.title}
            />
        </>
    );
}
